import { Save, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '../../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import {
  LEAD_TIME_MAX,
  LEAD_TIME_MIN,
  useInventorySettings,
  useUpdateInventorySettings,
} from '../../../hooks/inventory/useInventorySettings';

/**
 * Owner-editable inventory settings (#769). For v1 this is just the company-
 * wide reorder lead-time assumption. The route hosting this card is already
 * gated to ROLE_GROUPS.OWNERS so no per-control role check is needed.
 */
export function InventorySettingsCard({ className = '' }: { className?: string }) {
  const settingsQuery = useInventorySettings();
  const updateSettings = useUpdateInventorySettings();

  const [leadTimeInput, setLeadTimeInput] = useState<string>('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (settingsQuery.data) {
      setLeadTimeInput(String(settingsQuery.data.inventory_lead_time_days));
    }
  }, [settingsQuery.data]);

  const parsed = leadTimeInput.trim() === '' ? NaN : Number(leadTimeInput);
  const parsedInvalid =
    Number.isNaN(parsed) ||
    !Number.isInteger(parsed) ||
    parsed < LEAD_TIME_MIN ||
    parsed > LEAD_TIME_MAX;
  const dirty =
    !parsedInvalid && parsed !== (settingsQuery.data?.inventory_lead_time_days ?? null);

  const handleSave = async () => {
    if (parsedInvalid || !dirty) return;
    await updateSettings.mutateAsync({ inventory_lead_time_days: parsed });
    setSavedAt(Date.now());
  };

  return (
    <Card className={`bg-[#221F1F] border-[#504A4A] ${className}`}>
      <CardHeader>
        <CardTitle className="text-text-primary flex items-center gap-2">
          <Settings className="w-4 h-4 text-white/60" />
          Inventory settings
        </CardTitle>
      </CardHeader>
      <CardContent>
        <label htmlFor="inv-lead-time" className="text-white/60 text-sm block mb-1">
          Reorder lead time (days)
        </label>
        <p className="text-xs text-white/50 mb-3 max-w-prose">
          How long it takes to receive a reorder once placed. The system uses this — plus a 7-day
          safety buffer — to compute recommended reorder quantities and alert thresholds.
        </p>
        <div className="flex items-end gap-3">
          <div className="flex-1 max-w-[140px]">
            <Input
              id="inv-lead-time"
              type="number"
              min={LEAD_TIME_MIN}
              max={LEAD_TIME_MAX}
              step={1}
              inputMode="numeric"
              value={leadTimeInput}
              onChange={(e) => setLeadTimeInput(e.target.value)}
              aria-invalid={parsedInvalid}
              disabled={settingsQuery.isLoading}
            />
          </div>
          <Button
            onClick={handleSave}
            disabled={!dirty || parsedInvalid || updateSettings.isPending}
            className="gap-2"
          >
            <Save className="w-4 h-4" />
            {updateSettings.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
        {parsedInvalid && leadTimeInput !== '' && (
          <div className="text-red-400 text-xs mt-2">
            Enter a whole number between {LEAD_TIME_MIN} and {LEAD_TIME_MAX}.
          </div>
        )}
        {updateSettings.error && (
          <div className="text-red-400 text-xs mt-2">
            {(updateSettings.error as Error).message ?? 'Save failed.'}
          </div>
        )}
        {savedAt && !dirty && !updateSettings.isPending && !updateSettings.error && (
          <div className="text-emerald-400 text-xs mt-2">Saved.</div>
        )}
      </CardContent>
    </Card>
  );
}
